import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Layers,
  ListOrdered,
  Loader,
  MessageSquare,
  Pencil,
  Plus,
  Power,
  Search,
  SlidersHorizontal,
  Sparkles,
  Trash2,
  Wrench,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import PillTabs from '../../../components/common/PillTabs';
import ConfirmDialog from '../governed-mcp-gateway/components/ConfirmDialog';
import EmptyState from '../governed-mcp-gateway/components/EmptyState';
import { StatusBadge } from '../governed-mcp-gateway/components/StatusBadges';
import { fieldClass, primaryBtn } from '../governed-mcp-gateway/components/GatewayUi';
import {
  ALL_API_TYPES,
  apiTypeLabel,
  GATEWAY_STATUS_OPTIONS,
  LLM_GATEWAY_PATHS,
  PROVIDER_OPTIONS,
  llmTypeLabel,
  providerLabel,
} from './llmGatewayConstants';
import { logLlmGateway } from './llmGatewayLogger';
import { useLlmGatewayPage } from './LlmGatewayScreen';
import { useLlmGateway } from './llmGatewayStore';
import type { GatewayApiType, GatewayStatus, LlmGateway, LlmProvider, LlmType, RegisteredLlm } from './llmGatewayTypes';

const PAGE_SIZE = 12;

type TypeFilter = 'all' | LlmType;
type SortMode = 'name-asc' | 'name-desc';

const TYPE_TABS: { id: TypeFilter; label: string; icon: LucideIcon }[] = [
  { id: 'all', label: 'All', icon: Layers },
  { id: 'chat', label: 'Chat', icon: MessageSquare },
  { id: 'embedding', label: 'Embedding', icon: Layers },
  { id: 'multimodal', label: 'Multimodal', icon: Sparkles },
  { id: 'reranking', label: 'Reranking', icon: ListOrdered },
  { id: 'custom', label: 'Custom', icon: Wrench },
];

function typeIcon(type: LlmType): React.ComponentType<{ size?: number }> {
  return TYPE_TABS.find((tab) => tab.id === type)?.icon ?? Wrench;
}

function typeIconTone(type: LlmType, isLight: boolean): string {
  const tones: Record<LlmType, { light: string; dark: string }> = {
    chat: { light: 'bg-indigo-50 text-indigo-600', dark: 'bg-indigo-500/15 text-indigo-300' },
    embedding: { light: 'bg-sky-50 text-sky-600', dark: 'bg-sky-500/15 text-sky-300' },
    multimodal: { light: 'bg-violet-50 text-violet-600', dark: 'bg-violet-500/15 text-violet-300' },
    reranking: { light: 'bg-amber-50 text-amber-600', dark: 'bg-amber-500/15 text-amber-300' },
    custom: { light: 'bg-neutral-100 text-neutral-600', dark: 'bg-neutral-800 text-neutral-300' },
  };
  return isLight ? tones[type].light : tones[type].dark;
}

function Menu({
  open,
  onClose,
  children,
  isLight,
  align = 'right',
  root,
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  isLight: boolean;
  align?: 'left' | 'right';
  root: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest(`[data-menu-root="${root}"]`)) return;
      onClose();
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open, onClose, root]);
  if (!open) return null;
  return (
    <div
      className={`absolute z-20 mt-1.5 min-w-[220px] max-h-[22rem] overflow-auto rounded-xl border py-1 shadow-xl ${align === 'left' ? 'left-0' : 'right-0'} ${
        isLight ? 'bg-white border-neutral-200 shadow-neutral-900/10' : 'bg-neutral-900 border-neutral-700'
      }`}
    >
      {children}
    </div>
  );
}

function GatewayCard({
  gateway,
  model,
  isLight,
  onOpen,
  onEdit,
  onToggleStatus,
  onDelete,
}: {
  gateway: LlmGateway;
  model: RegisteredLlm | undefined;
  isLight: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onToggleStatus: () => void;
  onDelete: () => void;
}) {
  const Icon = typeIcon(gateway.llmType);
  const inactive = gateway.status === 'disabled' || gateway.status === 'draft';
  const extraApiCount = Math.max(0, gateway.apiTypes.length - 1);
  const firstApi = gateway.apiTypes[0];

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      className={`group relative flex flex-col rounded-2xl p-5 text-left cursor-pointer transition-all ${
        isLight
          ? 'bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] ring-1 ring-black/[0.04] hover:shadow-[0_8px_24px_rgba(16,24,40,0.08)] hover:-translate-y-0.5'
          : 'bg-neutral-900 ring-1 ring-white/10 hover:ring-white/20'
      } ${inactive ? 'opacity-70' : ''}`}
    >
      <div className="flex items-center gap-3 min-w-0 pr-16">
        <span className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${typeIconTone(gateway.llmType, isLight)}`}>
          <Icon size={18} />
        </span>
        <div className="min-w-0">
          <h3 className={`text-[15px] font-semibold tracking-tight truncate ${isLight ? 'text-neutral-900' : 'text-white'}`}>
            {gateway.name}
          </h3>
          <p className={`text-[11px] mt-0.5 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>{llmTypeLabel(gateway.llmType)}</p>
        </div>
      </div>

      <p className={`mt-3 text-sm leading-relaxed line-clamp-2 min-h-[2.5rem] ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
        {model?.name ?? gateway.modelId}
        {gateway.publicAlias ? ` · ${gateway.publicAlias}` : ''}
      </p>

      <div className={`mt-4 pt-4 border-t flex flex-wrap items-center gap-1.5 ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ${
            isLight ? 'bg-neutral-100 text-neutral-600' : 'bg-neutral-800 text-neutral-300'
          }`}
        >
          {providerLabel(gateway.provider)}
        </span>
        {firstApi ? (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ${
              isLight ? 'bg-neutral-100 text-neutral-600' : 'bg-neutral-800 text-neutral-300'
            }`}
          >
            {apiTypeLabel(firstApi)}
            {extraApiCount ? ` +${extraApiCount}` : ''}
          </span>
        ) : null}
        <span className="ml-auto">
          <StatusBadge status={gateway.status} />
        </span>
      </div>

      <div
        className={`absolute top-3.5 right-3.5 flex items-center gap-0.5 rounded-xl p-0.5 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto transition-opacity ${
          isLight ? 'bg-white/90 shadow-sm ring-1 ring-black/5' : 'bg-neutral-800 ring-1 ring-white/10'
        }`}
      >
        <button
          type="button"
          className={`p-1.5 rounded-lg ${isLight ? 'text-neutral-500 hover:text-indigo-600 hover:bg-indigo-50' : 'text-neutral-400 hover:text-white hover:bg-neutral-700'}`}
          aria-label="Edit"
          onClick={(event) => {
            event.stopPropagation();
            onEdit();
          }}
        >
          <Pencil size={14} />
        </button>
        <button
          type="button"
          className={`p-1.5 rounded-lg ${isLight ? 'text-neutral-500 hover:text-indigo-600 hover:bg-indigo-50' : 'text-neutral-400 hover:text-white hover:bg-neutral-700'}`}
          aria-label={gateway.status === 'disabled' ? 'Enable' : 'Disable'}
          onClick={(event) => {
            event.stopPropagation();
            onToggleStatus();
          }}
        >
          <Power size={14} />
        </button>
        <button
          type="button"
          className={`p-1.5 rounded-lg ${isLight ? 'text-neutral-500 hover:text-red-600 hover:bg-red-50' : 'text-neutral-400 hover:text-red-400 hover:bg-neutral-700'}`}
          aria-label="Delete"
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </article>
  );
}

export default function GatewayListPage() {
  const { isLight, onToast } = useLlmGatewayPage();
  const { gateways, getModel, ready, setStatus, deleteGateway } = useLlmGateway();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [provider, setProvider] = useState<LlmProvider | ''>('');
  const [apiType, setApiType] = useState<GatewayApiType | ''>('');
  const [status, setFilterStatus] = useState<GatewayStatus | ''>('');
  const [sort, setSort] = useState<SortMode>('name-asc');
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<LlmGateway | null>(null);

  useEffect(() => {
    logLlmGateway('app', 'list_open', { count: gateways.length });
  }, [gateways.length]);

  const filtered = useMemo(() => {
    const hay = query.trim().toLowerCase();
    const rows = gateways.filter((gateway) => {
      const model = getModel(gateway.modelId);
      const matchesType = typeFilter === 'all' || gateway.llmType === typeFilter;
      const matchesQuery =
        !hay ||
        gateway.name.toLowerCase().includes(hay) ||
        (model?.name ?? '').toLowerCase().includes(hay) ||
        gateway.publicAlias.toLowerCase().includes(hay);
      const matchesProvider = !provider || gateway.provider === provider;
      const matchesApi = !apiType || gateway.apiTypes.includes(apiType);
      const matchesStatus = !status || gateway.status === status;
      return matchesType && matchesQuery && matchesProvider && matchesApi && matchesStatus;
    });
    const direction = sort === 'name-desc' ? -1 : 1;
    return rows.slice().sort((left, right) => direction * left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }));
  }, [apiType, gateways, getModel, provider, query, sort, status, typeFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageStart = filtered.length ? (safePage - 1) * PAGE_SIZE + 1 : 0;
  const pageEnd = Math.min(safePage * PAGE_SIZE, filtered.length);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const filtersActive = Boolean(query || provider || apiType || status || typeFilter !== 'all');
  const filterCount = Number(Boolean(provider)) + Number(Boolean(apiType)) + Number(Boolean(status));
  const filterLabel = filterCount === 0
    ? 'Filters'
    : filterCount === 1
      ? provider
        ? providerLabel(provider)
        : apiType
          ? apiTypeLabel(apiType)
          : GATEWAY_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? 'Filters'
      : `${filterCount} filters`;

  const menuItem = (active: boolean) =>
    `w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-3 ${
      active
        ? isLight
          ? 'bg-indigo-50 text-indigo-700'
          : 'bg-indigo-500/15 text-indigo-300'
        : isLight
          ? 'text-neutral-700 hover:bg-neutral-50'
          : 'text-neutral-300 hover:bg-neutral-800'
    }`;

  const pillBtn = (active?: boolean) =>
    `inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm font-medium border transition-colors disabled:opacity-40 disabled:pointer-events-none ${
      active
        ? isLight
          ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
          : 'border-indigo-500/30 bg-indigo-500/15 text-indigo-300'
        : isLight
          ? 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50'
          : 'border-neutral-700 bg-neutral-900 text-neutral-300 hover:bg-neutral-800'
    }`;

  const sectionLabel = `px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`;

  const toggleStatus = (gateway: LlmGateway) => {
    const next = gateway.status === 'disabled' ? 'active' : 'disabled';
    void (async () => {
      try {
        await setStatus(gateway.id, next);
        onToast('success', next === 'disabled' ? 'Gateway disabled.' : 'Gateway enabled.');
      } catch (error) {
        onToast('error', error instanceof Error ? error.message : 'Could not update gateway status.');
      }
    })();
  };

  if (!ready) {
    return (
      <div className={`flex items-center justify-center py-24 text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
        <Loader size={16} className="animate-spin mr-2" />
        Loading gateways…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PillTabs
          isLight={isLight}
          ariaLabel="LLM types"
          className="flex-wrap"
          value={typeFilter}
          items={TYPE_TABS.map((tab) => ({
            ...tab,
            count: tab.id === 'all' ? gateways.length : gateways.filter((gateway) => gateway.llmType === tab.id).length,
          }))}
          onChange={(id) => {
            setTypeFilter(id);
            setPage(1);
            logLlmGateway('app', 'list_type_tab', { type: id });
          }}
        />
        <button type="button" className={`${primaryBtn()} rounded-full`} onClick={() => navigate(LLM_GATEWAY_PATHS.create)}>
          <Plus size={14} />
          Create Gateway
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className={`absolute left-3.5 top-1/2 -translate-y-1/2 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`} />
          <input
            className={`${fieldClass(isLight)} pl-10 rounded-full ${isLight ? 'bg-white' : ''}`}
            placeholder="Search gateways or models…"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="relative" data-menu-root="filters">
          <button type="button" className={pillBtn(filterCount > 0)} onClick={() => setFiltersOpen((value) => !value)}>
            <SlidersHorizontal size={14} />
            {filterLabel}
            <ChevronDown size={13} />
          </button>
          <Menu open={filtersOpen} onClose={() => setFiltersOpen(false)} isLight={isLight} align="left" root="filters">
            <p className={sectionLabel}>Provider</p>
            <button
              type="button"
              className={menuItem(!provider)}
              onClick={() => {
                setProvider('');
                setPage(1);
              }}
            >
              All providers
            </button>
            {PROVIDER_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={menuItem(provider === option.value)}
                onClick={() => {
                  setProvider(option.value);
                  setPage(1);
                }}
              >
                {option.label}
              </button>
            ))}
            <div className={`my-1 h-px ${isLight ? 'bg-neutral-100' : 'bg-neutral-800'}`} />
            <p className={sectionLabel}>API type</p>
            <button
              type="button"
              className={menuItem(!apiType)}
              onClick={() => {
                setApiType('');
                setPage(1);
              }}
            >
              All API types
            </button>
            {ALL_API_TYPES.map((id) => (
              <button
                key={id}
                type="button"
                className={menuItem(apiType === id)}
                onClick={() => {
                  setApiType(id);
                  setPage(1);
                }}
              >
                {apiTypeLabel(id)}
              </button>
            ))}
            <div className={`my-1 h-px ${isLight ? 'bg-neutral-100' : 'bg-neutral-800'}`} />
            <p className={sectionLabel}>Status</p>
            <button
              type="button"
              className={menuItem(!status)}
              onClick={() => {
                setFilterStatus('');
                setPage(1);
              }}
            >
              All statuses
            </button>
            {GATEWAY_STATUS_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={menuItem(status === option.value)}
                onClick={() => {
                  setFilterStatus(option.value);
                  setPage(1);
                }}
              >
                {option.label}
              </button>
            ))}
          </Menu>
        </div>
        <div className={`inline-flex items-center gap-1 text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
          <span className="tabular-nums px-1">
            {pageStart}–{pageEnd} of {filtered.length}
          </span>
          <button
            type="button"
            className={pillBtn()}
            disabled={safePage <= 1}
            onClick={() => setPage((value) => Math.max(1, value - 1))}
            aria-label="Previous page"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            type="button"
            className={pillBtn()}
            disabled={safePage >= pageCount}
            onClick={() => setPage((value) => Math.min(pageCount, value + 1))}
            aria-label="Next page"
          >
            <ChevronRight size={14} />
          </button>
        </div>
        <div className="relative ml-auto" data-menu-root="sort">
          <button type="button" className={pillBtn()} onClick={() => setSortOpen((value) => !value)}>
            {sort === 'name-desc' ? 'Name Z–A' : 'Name A–Z'}
            <ChevronDown size={13} />
          </button>
          <Menu open={sortOpen} onClose={() => setSortOpen(false)} isLight={isLight} root="sort">
            <button
              type="button"
              className={menuItem(sort === 'name-asc')}
              onClick={() => {
                setSort('name-asc');
                setSortOpen(false);
              }}
            >
              Name A–Z
            </button>
            <button
              type="button"
              className={menuItem(sort === 'name-desc')}
              onClick={() => {
                setSort('name-desc');
                setSortOpen(false);
              }}
            >
              Name Z–A
            </button>
          </Menu>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          isLight={isLight}
          title={filtersActive ? 'No matching gateways' : 'No LLM gateways yet'}
          description={
            filtersActive
              ? 'Try a different search or clear the type, provider, API type, or status filters.'
              : 'Create a gateway to publish a governed endpoint in front of a registered model.'
          }
          action={
            filtersActive ? null : (
              <button type="button" className={`${primaryBtn()} rounded-full`} onClick={() => navigate(LLM_GATEWAY_PATHS.create)}>
                <Plus size={14} />
                Create Gateway
              </button>
            )
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {pageItems.map((gateway) => (
            <GatewayCard
              key={gateway.id}
              gateway={gateway}
              model={getModel(gateway.modelId)}
              isLight={isLight}
              onOpen={() => navigate(LLM_GATEWAY_PATHS.detail(gateway.id))}
              onEdit={() => navigate(LLM_GATEWAY_PATHS.edit(gateway.id))}
              onToggleStatus={() => toggleStatus(gateway)}
              onDelete={() => setPendingDelete(gateway)}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete gateway"
        description={`Delete ${pendingDelete?.name ?? 'this gateway'}? Connection URLs and issued keys will stop working.`}
        confirmLabel="Delete"
        danger
        isLight={isLight}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) return;
          const target = pendingDelete;
          void (async () => {
            try {
              await deleteGateway(target.id);
              logLlmGateway('warning', 'gateway_delete_confirmed', { id: target.id });
              onToast('success', 'Gateway deleted.');
              setPendingDelete(null);
            } catch (error) {
              onToast('error', error instanceof Error ? error.message : 'Could not delete this gateway.');
            }
          })();
        }}
      />
    </div>
  );
}
